import type { DocumentGroupingMode, DocumentSortMode, BrowsableDocument } from './useDocumentBrowserStore';
import { getDocumentStatusPresentation } from './DocumentStatusBadge';
import { formatDocumentTypeLabel } from './documentBrowserUtils';

export interface DocumentGroup {
  key: string;
  label: string;
  order: number;
  documents: BrowsableDocument[];
}

interface GroupDescriptor {
  key: string;
  label: string;
  order: number;
}

const STATUS_GROUP_PRIORITY: Record<string, number> = {
  active: 10,
  activeWithoutEmbeddings: 20,
  review: 30,
  processing: 40,
  partiallyFailed: 50,
  failed: 60,
  empty: 70,
  deleted: 80,
  other: 90,
};

export function getSortedDocuments(documents: BrowsableDocument[], sortBy: DocumentSortMode): BrowsableDocument[] {
  return [...documents].sort((left, right) => compareDocuments(left, right, sortBy));
}

export function getDocumentGroups(
  documents: BrowsableDocument[],
  groupBy: Exclude<DocumentGroupingMode, 'none'>,
  sortBy: DocumentSortMode,
): DocumentGroup[] {
  const groups = new Map<string, DocumentGroup>();

  for (const document of getSortedDocuments(documents, sortBy)) {
    const descriptor = getDocumentGroupDescriptor(document, groupBy);
    const existing = groups.get(descriptor.key);

    if (existing) {
      existing.documents.push(document);
      continue;
    }

    groups.set(descriptor.key, {
      ...descriptor,
      documents: [document],
    });
  }

  return Array.from(groups.values()).sort((left, right) => compareGroups(left, right));
}

function compareDocuments(left: BrowsableDocument, right: BrowsableDocument, sortBy: DocumentSortMode) {
  switch (sortBy) {
    case 'createdAtAsc':
      return compareDates(left.createdAt, right.createdAt);
    case 'fileNameAsc':
      return left.fileName.localeCompare(right.fileName, 'de');
    case 'fileNameDesc':
      return right.fileName.localeCompare(left.fileName, 'de');
    case 'createdAtDesc':
    default:
      return compareDates(right.createdAt, left.createdAt);
  }
}

function compareDates(leftIso: string, rightIso: string) {
  return new Date(leftIso).getTime() - new Date(rightIso).getTime();
}

function compareGroups(left: DocumentGroup, right: DocumentGroup) {
  if (left.order !== right.order) {
    return left.order - right.order;
  }

  return left.label.localeCompare(right.label, 'de');
}

function getDocumentGroupDescriptor(document: BrowsableDocument, groupBy: Exclude<DocumentGroupingMode, 'none'>): GroupDescriptor {
  switch (groupBy) {
    case 'machine': {
      return {
        key: document.machineModelId ?? '__unassigned_machine__',
        label: document.machineLabel ?? 'Keine Maschine zugeordnet',
        order: 100,
      };
    }
    case 'documentType': {
      return {
        key: document.documentType ?? '__untyped__',
        label: formatDocumentTypeLabel(document.documentType),
        order: 100,
      };
    }
    case 'status':
    default:
      return getStatusGroupDescriptor(document.status);
  }
}

function getStatusGroupDescriptor(status: string): GroupDescriptor {
  const presentation = getDocumentStatusPresentation(status);

  switch (status) {
    case 'Completed':
      return { key: 'active', label: presentation.label, order: STATUS_GROUP_PRIORITY.active };
    case 'CompletedWithoutEmbeddings':
      return { key: 'activeWithoutEmbeddings', label: presentation.label, order: STATUS_GROUP_PRIORITY.activeWithoutEmbeddings };
    case 'AwaitingConfirmation':
      return { key: 'review', label: presentation.label, order: STATUS_GROUP_PRIORITY.review };
    case 'Uploaded':
    case 'Processing':
    case 'Parsing':
    case 'Parsed':
    case 'Extracting':
      return { key: 'processing', label: presentation.label, order: STATUS_GROUP_PRIORITY.processing };
    case 'PartiallyFailed':
      return { key: 'partiallyFailed', label: presentation.label, order: STATUS_GROUP_PRIORITY.partiallyFailed };
    case 'Failed':
      return { key: 'failed', label: presentation.label, order: STATUS_GROUP_PRIORITY.failed };
    case 'EmptyDocument':
      return { key: 'empty', label: presentation.label, order: STATUS_GROUP_PRIORITY.empty };
    case 'Deleted':
      return { key: 'deleted', label: presentation.label, order: STATUS_GROUP_PRIORITY.deleted };
    default:
      return { key: status, label: presentation.label, order: STATUS_GROUP_PRIORITY.other };
  }
}